import { Link, useLocation } from "@tanstack/react-router";
import type { LucideIcon } from "lucide-react";
import {
  ArrowRight,
  BadgePercent,
  BookOpen,
  CalendarCheck,
  Coins,
  Flame,
  Gift,
  HeartHandshake,
  History,
  Inbox,
  Landmark,
  Layers,
  MessageCircle,
  Mic,
  ScrollText,
  ShieldCheck,
  Sparkles,
  Star,
  Trophy,
  Users,
  Wallet,
} from "lucide-react";

// Landing SEO cho các trang tính năng (khu vực _authenticated) khi CHƯA đăng nhập:
// SSR luôn render component này (server không đọc được localStorage) nên bot cào
// được HTML thật thay vì spinner/redirect về /login. Meta title/description đã
// nằm ở head() của từng route — ở đây chỉ lo phần nội dung indexable + CTA.

type Highlight = { icon: LucideIcon; title: string; desc: string };

type LandingConfig = {
  badge: string;
  title: string;
  accent: string; // vế được tô gold-gradient trong h1
  description: string;
  highlights: Highlight[];
  learnMore?: { label: string; to: string };
};

const LANDINGS: Array<{ prefix: string; config: LandingConfig }> = [
  {
    prefix: "/kinh-dich",
    config: {
      badge: "Kinh Dịch · Mai Hoa Dịch Số",
      title: "Gieo quẻ Kinh Dịch online,",
      accent: "luận giải bằng AI",
      description:
        "Tham vấn 64 quẻ Dịch theo phương pháp Mai Hoa Dịch Số cổ truyền: an quẻ theo giờ động tâm, xác định quẻ chủ, hào động và quẻ biến, rồi nhận lời luận giải chi tiết cùng lời khuyên cụ thể từ thầy bói AI.",
      highlights: [
        {
          icon: Flame,
          title: "Khởi tâm lập ý",
          desc: "Đặt một câu hỏi cụ thể cho một sự việc — chuyện tình cảm, công việc, tiền bạc hay quyết định quan trọng sắp tới.",
        },
        {
          icon: Sparkles,
          title: "An quẻ theo giờ động tâm",
          desc: "Máy gieo quẻ theo đúng phép Mai Hoa Dịch Số: tính can chi thời điểm tâm khởi, đủ Lục Thân, Tuần Không, hào động.",
        },
        {
          icon: ScrollText,
          title: "Luận giải trọn vẹn",
          desc: "Lời quẻ chính, tượng quẻ, hào động và quẻ biến được diễn giải mạch lạc kèm lời khuyên hành động rõ ràng.",
        },
        {
          icon: MessageCircle,
          title: "Hỏi tiếp đến khi tỏ",
          desc: "Trò chuyện trực tiếp với thầy bói AI theo mạch quẻ vừa gieo — hỏi sâu từng hào, từng biến cố.",
        },
      ],
      learnMore: { label: "Tìm hiểu về Kinh Dịch", to: "/kinh-dich-chi-tiet" },
    },
  },
  {
    prefix: "/tong-hop",
    config: {
      badge: "Bói Đê · Xem theo chủ đề",
      title: "Một câu hỏi,",
      accent: "một lời luận sát ý",
      description:
        "Nhập điều bạn đang băn khoăn, thỉnh gói Thiên Cơ phù hợp và nhận lời luận theo đúng chủ đề. Bạn có thể bật gieo quẻ Kinh Dịch nếu muốn xem thêm quẻ.",
      highlights: [
        {
          icon: Layers,
          title: "Gói Thiên Cơ dễ chọn",
          desc: "Tình duyên, công danh, gia đình, sức khỏe… được gom trong một nút và lọc theo nhóm.",
        },
        {
          icon: Sparkles,
          title: "Gieo quẻ theo ý bạn",
          desc: "Toggle gieo quẻ mặc định tắt; bật lên khi muốn thêm quẻ Mai Hoa vào lời luận.",
        },
        {
          icon: MessageCircle,
          title: "Hỏi tiếp đến khi tỏ",
          desc: "Sau lời luận trọn gói, trò chuyện tiếp với thầy theo đúng lá số và quẻ vừa lập — bối cảnh giữ nguyên xuyên suốt.",
        },
      ],
      learnMore: { label: "Lập lá số Tử Vi miễn phí", to: "/tu-vi" },
    },
  },
  {
    prefix: "/se-duyen",
    config: {
      badge: "Se duyên & hợp tác làm ăn",
      title: "Kết nối hữu duyên theo",
      accent: "tương hợp Tử Vi, ngũ hành",
      description:
        "Tìm người yêu hoặc đối tác làm ăn dựa trên mệnh lý Đông phương: tam hợp, lục hợp 12 con giáp, ngũ hành nạp âm và lá số Tử Vi. Hồ sơ ẩn danh, chỉ chia sẻ thông tin khi cả hai bên đồng ý kết nối.",
      highlights: [
        {
          icon: HeartHandshake,
          title: "Ghép đôi theo mệnh lý",
          desc: "Điểm tương hợp tính từ con giáp, ngũ hành nạp âm và cung Phu Thê / Nô Bộc trong lá số Tử Vi.",
        },
        {
          icon: ShieldCheck,
          title: "Riêng tư hai tầng",
          desc: "Feed quẹt hoàn toàn ẩn danh; ngày sinh, liên hệ chỉ lộ sau khi ghép đôi và chính bạn chọn chia sẻ từng trường.",
        },
        {
          icon: Users,
          title: "Hai mục đích rõ ràng",
          desc: "Se duyên đôi lứa hoặc tìm người hợp tác làm ăn — mỗi mục đích có bộ trọng số tương hợp riêng.",
        },
        {
          icon: Sparkles,
          title: "Luận giải tương hợp đầy đủ",
          desc: "Sau khi kết nối, nhận bản luận giải chi tiết hai lá số: điểm mạnh, điểm cần dung hoà và lời khuyên.",
        },
      ],
      learnMore: { label: "Lập lá số Tử Vi miễn phí", to: "/tu-vi" },
    },
  },
  {
    prefix: "/boi-live",
    config: {
      badge: "Bói Live",
      title: "Đàm đạo trực tiếp bằng giọng nói",
      accent: "với thầy bói AI",
      description:
        "Gọi thoại realtime với thầy bói AI như một buổi xem thật: thầy nghe bạn kể, gieo quẻ Kinh Dịch, lập lá số Tử Vi và Bát Tự ngay trong lúc trò chuyện rồi luận giải bằng giọng nói tự nhiên. Chỉ 7.500đ/phút, tính theo giây.",
      highlights: [
        {
          icon: Mic,
          title: "Trò chuyện như người thật",
          desc: "Giọng nói tự nhiên theo từng nhân vật thầy bói, nghe — đáp tức thì, có thể ngắt lời hỏi thêm bất cứ lúc nào.",
        },
        {
          icon: Sparkles,
          title: "Gieo quẻ, lập lá số trong cuộc gọi",
          desc: "Thầy tự gieo quẻ Kinh Dịch, lập lá số Tử Vi / Bát Tự khi cần — kết quả hiện ngay trên màn hình.",
        },
        {
          icon: BookOpen,
          title: "Lưu trọn buổi xem",
          desc: "Transcript, quẻ, lá số và ghi chú buổi xem được lưu lại — mở xem lại bất cứ lúc nào.",
        },
        {
          icon: Coins,
          title: "Tính tiền theo giây",
          desc: "7.500đ/phút, trừ chính xác theo giây trò chuyện — dừng lúc nào tính tiền lúc đó.",
        },
      ],
      learnMore: { label: "Tìm hiểu về Kinh Dịch", to: "/kinh-dich-chi-tiet" },
    },
  },
  {
    prefix: "/diem-danh",
    config: {
      badge: "Điểm danh mỗi ngày",
      title: "Điểm danh nhận thưởng,",
      accent: "giữ chuỗi bói mỗi ngày",
      description:
        "Mỗi ngày điểm danh một lần để nhận thưởng vào ví bói theo chu kỳ 7 ngày, đồng thời duy trì chuỗi bói và leo bảng xếp hạng cùng cộng đồng.",
      highlights: [
        {
          icon: CalendarCheck,
          title: "Chu kỳ 7 ngày",
          desc: "Ngày 1–6 nhận 1.000đ mỗi ngày, ngày thứ 7 nhận 5.000đ rồi chu kỳ lặp lại — bỏ lỡ một ngày là quay về đầu.",
        },
        {
          icon: Flame,
          title: "Chuỗi bói",
          desc: "Mỗi ngày có ít nhất một lượt gieo quẻ hoặc lập lá số là chuỗi tăng thêm một — giữ lửa mỗi ngày.",
        },
        {
          icon: Trophy,
          title: "Bảng xếp hạng",
          desc: "Đua chuỗi theo tuần, tháng hoặc mọi thời đại cùng cộng đồng Bói Đê.",
        },
      ],
    },
  },
  {
    prefix: "/gioi-thieu",
    config: {
      badge: "Giới thiệu & hoa hồng",
      title: "Giới thiệu bạn bè, nhận",
      accent: "10% hoa hồng trọn đời",
      description:
        "Tự đặt mã giới thiệu của riêng bạn, chia sẻ link cho bạn bè. Mỗi lần người được giới thiệu nạp tiền — dù là lần thứ bao nhiêu — bạn nhận 10% hoa hồng; họ cũng được +10% cho lần nạp đầu.",
      highlights: [
        {
          icon: BadgePercent,
          title: "10% trọn đời",
          desc: "Hoa hồng tính trên mọi lần nạp của người bạn giới thiệu, không giới hạn thời gian.",
        },
        {
          icon: Gift,
          title: "Đôi bên cùng lợi",
          desc: "Người được giới thiệu nhận thêm 10% giá trị cho lần nạp đầu tiên.",
        },
        {
          icon: Landmark,
          title: "Rút về ngân hàng",
          desc: "Hoa hồng chuyển được sang ví bói để dùng ngay, hoặc rút thẳng về tài khoản ngân hàng.",
        },
      ],
      learnMore: { label: "Xem hướng dẫn hoa hồng chi tiết", to: "/huong-dan-hoa-hong" },
    },
  },
  {
    prefix: "/lich-su",
    config: {
      badge: "Lịch sử tham vấn",
      title: "Xem lại mọi quẻ Dịch,",
      accent: "lá số đã lập",
      description:
        "Toàn bộ quẻ Kinh Dịch đã gieo, lá số Tử Vi, Bát Tự đã lập và các phiên trò chuyện được lưu lại trong tài khoản — mở lại và tiếp tục luận giải bất cứ lúc nào.",
      highlights: [
        {
          icon: History,
          title: "Lưu trọn hành trình",
          desc: "Mỗi quẻ, mỗi lá số, mỗi phiên hỏi đáp đều nằm gọn một chỗ, sắp theo thời gian.",
        },
        {
          icon: MessageCircle,
          title: "Tiếp tục mạch hỏi",
          desc: "Mở lại phiên cũ và hỏi tiếp — thầy vẫn nhớ ngữ cảnh quẻ và lá số của phiên đó.",
        },
      ],
    },
  },
  {
    prefix: "/thanh-toan",
    config: {
      badge: "Nạp tiền",
      title: "Nạp tiền nhanh chóng",
      accent: "qua mã QR ngân hàng",
      description:
        "Quét mã QR chuyển khoản ngân hàng, hệ thống tự động cộng số dư trong vài giây — dùng để gieo quẻ, khai mở lá số, Bói Live và các dịch vụ khác. Có mã quà tặng? Nhập tại đây để đổi thưởng.",
      highlights: [
        {
          icon: Wallet,
          title: "Tự động tức thì",
          desc: "Chuyển khoản đúng nội dung là số dư được cộng tự động, không cần chờ duyệt tay.",
        },
        {
          icon: Gift,
          title: "Đổi mã quà tặng",
          desc: "Nhập gifcode nhận thưởng trực tiếp vào số dư.",
        },
      ],
    },
  },
  {
    prefix: "/hop-thu",
    config: {
      badge: "Hộp thư",
      title: "Thông báo, quà tặng và",
      accent: "kênh góp ý trực tiếp",
      description:
        "Nhận thông báo hệ thống kèm quà thưởng vào số dư, đồng thời gửi góp ý, khiếu nại hoặc đề xuất trực tiếp cho đội ngũ Bói Đê ngay trong hộp thư tài khoản.",
      highlights: [
        {
          icon: Inbox,
          title: "Thư kèm quà",
          desc: "Sự kiện, đền bù, quà tặng — bấm nhận là số dư được cộng ngay.",
        },
        {
          icon: MessageCircle,
          title: "Hai chiều",
          desc: "Gửi khiếu nại / đề xuất kèm ảnh chụp màn hình, admin trả lời thẳng vào hộp thư của bạn.",
        },
      ],
    },
  },
  {
    prefix: "/bien-dong-so-du",
    config: {
      badge: "Biến động số dư",
      title: "Minh bạch từng giao dịch",
      accent: "trong tài khoản",
      description:
        "Theo dõi lịch sử nạp tiền, chi tiêu cho từng lượt xem và mọi khoản thưởng — mỗi biến động số dư đều được ghi lại rõ ràng theo thời gian.",
      highlights: [
        {
          icon: Wallet,
          title: "Ghi lại tất cả",
          desc: "Nạp tiền, gieo quẻ, khai mở lá số, thưởng điểm danh, hoa hồng — không sót khoản nào.",
        },
        {
          icon: ShieldCheck,
          title: "Rõ ràng minh bạch",
          desc: "Mỗi dòng có loại giao dịch, số tiền và thời điểm — đối soát dễ dàng.",
        },
      ],
    },
  },
  {
    prefix: "/cam-nhan",
    config: {
      badge: "Chia sẻ cảm nhận",
      title: "Kể trải nghiệm của bạn,",
      accent: "nhận thưởng lớn",
      description:
        "Đăng bài cảm nhận về Bói Đê lên mạng xã hội, gửi link bài viết cho chúng tôi — bài được duyệt là nhận thưởng thẳng vào số dư.",
      highlights: [
        {
          icon: Star,
          title: "Viết thật, thưởng thật",
          desc: "Chia sẻ trải nghiệm gieo quẻ, xem lá số của chính bạn trên Facebook, TikTok, Threads...",
        },
        {
          icon: Gift,
          title: "Thưởng vào số dư",
          desc: "Bài viết được duyệt là tiền thưởng cộng thẳng vào số dư để tiếp tục trải nghiệm.",
        },
      ],
    },
  },
];

const GENERIC: LandingConfig = {
  badge: "Bói Đê",
  title: "Huyền học Đông phương,",
  accent: "luận giải bằng AI",
  description:
    "Gieo quẻ Kinh Dịch theo Mai Hoa Dịch Số, lập lá số Tử Vi và Bát Tự chuẩn lịch Việt Nam, trò chuyện trực tiếp với thầy bói AI. Đăng nhập để bắt đầu hành trình tham vấn vận mệnh của bạn.",
  highlights: [
    {
      icon: Sparkles,
      title: "Kinh Dịch · Mai Hoa",
      desc: "Gieo quẻ theo giờ động tâm, luận quẻ chủ, hào động, quẻ biến cùng AI.",
    },
    {
      icon: ScrollText,
      title: "Tử Vi & Bát Tự",
      desc: "Lá số an chuẩn lịch âm Việt Nam, chấm điểm 12 cung và khai mở luận giải chi tiết.",
    },
    {
      icon: Mic,
      title: "Bói Live",
      desc: "Đàm đạo bằng giọng nói realtime với thầy bói AI như một buổi xem thật.",
    },
  ],
};

// Các trang public liên quan — internal linking cho SEO.
const EXPLORE_LINKS: Array<{ label: string; to: string }> = [
  { label: "Lập lá số Tử Vi", to: "/tu-vi" },
  { label: "Lập lá số Bát Tự", to: "/bat-tu" },
  { label: "Kinh Dịch là gì?", to: "/kinh-dich-chi-tiet" },
  { label: "Câu hỏi thường gặp", to: "/faq" },
];

export function FeatureLanding() {
  const pathname = useLocation({ select: (l) => l.pathname });
  const entry = LANDINGS.find((e) => pathname === e.prefix || pathname.startsWith(`${e.prefix}/`));
  const config = entry?.config ?? GENERIC;

  return (
    <main className="min-h-0 flex-1 overflow-y-auto px-4 py-8 pb-24 md:px-8 md:py-12 md:pb-12">
      <div className="mx-auto max-w-4xl space-y-6">
        {/* Hero */}
        <section className="rounded-3xl border border-gold-soft bg-card/70 p-6 text-center shadow-mystic backdrop-blur md:p-10">
          <span className="font-sans text-[11px] font-semibold uppercase tracking-[0.18em] text-primary">
            {config.badge}
          </span>
          <h1 className="mt-3 font-serif-soft text-3xl leading-snug text-foreground md:text-4xl">
            {config.title} <span className="text-gold-gradient">{config.accent}</span>
          </h1>
          <p className="mx-auto mt-4 max-w-2xl text-sm leading-relaxed text-muted-foreground md:text-base">
            {config.description}
          </p>
          <div className="mt-6 flex flex-wrap items-center justify-center gap-3">
            <Link
              to="/login"
              search={{ returnTo: pathname }}
              className="group inline-flex items-center gap-2 rounded-full bg-gold-gradient px-6 py-3 text-sm font-semibold text-primary-foreground shadow-gold transition hover:scale-[1.02] active:scale-95"
            >
              Đăng nhập để bắt đầu
              <ArrowRight className="h-4 w-4 transition group-hover:translate-x-0.5" />
            </Link>
            {config.learnMore && (
              <Link
                to={config.learnMore.to}
                className="inline-flex items-center gap-2 rounded-full border border-gold-soft bg-card/60 px-6 py-3 text-sm font-medium text-foreground backdrop-blur transition hover:border-primary/60 hover:text-primary"
              >
                {config.learnMore.label}
              </Link>
            )}
          </div>
          <p className="mt-4 text-xs text-muted-foreground/80">
            Đăng nhập nhanh bằng Google — tài khoản mới được tặng sẵn số dư để trải nghiệm.
          </p>
        </section>

        {/* Điểm nổi bật */}
        <section className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          {config.highlights.map((h) => (
            <article
              key={h.title}
              className="rounded-2xl border border-gold-soft/50 bg-card/60 p-5 backdrop-blur"
            >
              <h.icon className="h-6 w-6 text-primary" />
              <h2 className="mt-3 font-serif-soft text-lg text-foreground">{h.title}</h2>
              <p className="mt-2 text-sm leading-relaxed text-muted-foreground">{h.desc}</p>
            </article>
          ))}
        </section>

        {/* Khám phá thêm — internal linking các trang public */}
        <section className="rounded-2xl border border-gold-soft/50 bg-card/60 p-5 backdrop-blur">
          <h2 className="font-serif-soft text-lg text-foreground">
            Khám phá thêm — không cần đăng nhập
          </h2>
          <div className="mt-3 flex flex-wrap gap-2">
            {EXPLORE_LINKS.map((l) => (
              <Link
                key={l.to}
                to={l.to}
                className="rounded-full border border-gold-soft/60 bg-background/40 px-4 py-1.5 text-sm text-muted-foreground transition hover:border-primary/60 hover:text-primary"
              >
                {l.label}
              </Link>
            ))}
            <a
              href="/blog"
              className="rounded-full border border-gold-soft/60 bg-background/40 px-4 py-1.5 text-sm text-muted-foreground transition hover:border-primary/60 hover:text-primary"
            >
              Blog huyền học
            </a>
          </div>
        </section>
      </div>
    </main>
  );
}
